import { environment } from "@/../environments/environment";
import { IframeDialogComponent } from "@/components/iframe-dialog/iframe-dialog.component";
import { AppConstants } from "@/helpers/app.constants";
import { PropertyHelper } from "@/helpers/property.helper";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { AuthenticationService } from "@/services/authentication.service";
import { EmailWithTLDValidator } from "@/validators/EmailWithTLDValidator";
import { UserNameValidator } from "@/validators/UserNameValidator";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { ErrorBusinessCode, UserRole, UsersService } from "@eemo/api-client";
import { MdbPopoverModule } from "mdb-angular-ui-kit/popover";
import { ReCaptchaV3Service } from "ng-recaptcha";
import { Subscription } from "rxjs";
import { first } from "rxjs/operators";

import { PasswordCheckComponent } from "@/components/password-check/password-check.component";
import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { FormHelper } from "@/helpers/formHelper";
import { UserHelper } from "@/helpers/user.helper";
import { PhoneNumberValidator } from "@/validators/PhoneNumberValidator";
import { NgIf, NgTemplateOutlet } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { TranslateModule } from "@ngx-translate/core";
import { CardBaseComponent } from "../../../components/base/card-base/card-base.component";

@Component({
  templateUrl: "./register.component.html",
  styleUrls: ["./register.component.scss"],
  standalone: true,
  imports: [
    CardBaseComponent,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    MatIconModule,
    MdbPopoverModule,
    MatCheckboxModule,
    MatButtonModule,
    RouterLink,
    TooltipDirective,
    PasswordCheckComponent,
    TranslateModule,
  ],
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: UntypedFormGroup;
  loading = false;
  submitted = false;
  hidePassword = true;
  hidePasswordConfirm = true;

  email = "";

  cguUrl = environment.CGU_URL;
  cguDocName = "EEMO CGU";

  recaptchaSub = new Subscription();
  registerSub = new Subscription();
  passwordChangeSub = new Subscription();
  passwordConfirmedChangeSub = new Subscription();

  returnUrl: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private userService: UsersService,
    private userHelper: UserHelper,
    private alertService: AlertService,
    public appConfigService: AppConfigService,
    private propHelper: PropertyHelper,
    private recaptchaV3Service: ReCaptchaV3Service,
    private renderer: Renderer2,
    private dialog: MatDialog
  ) {
    // redirect to home if already logged in
    if (this.authenticationService.currentUserValue) {
      this.router.navigate(["/"]);
    }
  }

  get form_password() {
    return this.registerForm.get("password") as FormControl<string>;
  }
  get form_passwordConfirmed() {
    return this.registerForm.get("passwordConfirmed") as FormControl<string>;
  }
  get form_phoneNumber() {
    return this.registerForm.get("phoneNumber") as FormControl<string>;
  }

  ngOnInit() {
    if (!this.appConfigService.isMobileApp) {
      this.renderer.addClass(document.body, "recaptcha");
      this.renderer.addClass(document.body, "httpcs");
    }
    this.returnUrl =
      this.route.snapshot.queryParams[AppConstants.PARAM_RETURN_URL] || "/";
    this.email = this.route.snapshot.queryParams[AppConstants.PARAM_EMAIL];
    let defaultRole =
      this.route.snapshot.queryParams[AppConstants.PARAM_USER_ROLE];
    if (
      !defaultRole ||
      !this.userHelper.getAllowedRoleForRegister().includes(defaultRole)
    ) {
      defaultRole = UserRole.INDIVIDUAL;
    }
    this.registerForm = this.formBuilder.group({
      role: [defaultRole, Validators.required],
      firstName: ["", [Validators.maxLength(50), UserNameValidator]],
      name: [
        "",
        [Validators.required, Validators.maxLength(50), UserNameValidator],
      ],
      email: [this.email, [Validators.required, EmailWithTLDValidator]],
      phoneNumber: [null, PhoneNumberValidator],
      password: [
        "",
        [
          Validators.required,
          Validators.pattern(AppConstants.FORM_PATTERN_PASSWORD),
        ],
      ],
      passwordConfirmed: ["", Validators.required],
      cgu: [false, Validators.required],
    });

    this.passwordChangeSub = this.form_password.valueChanges.subscribe(() => {
      if (this.form_password.hasError("passwordDontMatch"))
        this.form_password.setErrors(null);
      if (this.form_passwordConfirmed.hasError("passwordDontMatch"))
        this.form_passwordConfirmed.setErrors(null);
    });
    this.passwordConfirmedChangeSub =
      this.form_passwordConfirmed.valueChanges.subscribe(() => {
        if (this.form_password.hasError("passwordDontMatch"))
          this.form_password.setErrors(null);
        if (this.form_passwordConfirmed.hasError("passwordDontMatch"))
          this.form_passwordConfirmed.setErrors(null);
      });
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, "recaptcha");
    this.renderer.removeClass(document.body, "httpcs");
    this.recaptchaSub.unsubscribe();
    this.registerSub.unsubscribe();
    this.passwordChangeSub.unsubscribe();
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.registerForm.get(field);
  }

  onSubmit() {
    // reset alerts on submit
    this.alertService.clear();

    if (this.f("cgu").value !== true) {
      this.f("cgu").setErrors({ required: true });
    }

    if (this.f("password").value !== this.f("passwordConfirmed").value) {
      this.f("password").setErrors({ passwordDontMatch: true });
      this.f("passwordConfirmed").setErrors({ passwordDontMatch: true });
    } else {
      this.f("password").setErrors({});
      this.f("password").updateValueAndValidity({ onlySelf: false });
      this.f("passwordConfirmed").setErrors({});
      this.f("passwordConfirmed").updateValueAndValidity({ onlySelf: false });
    }

    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // force the email to lowercase and trim
    this.f("email").setValue(this.f("email").value.toLowerCase().trim());
    // replace spaces by - in names
    this.f("firstName").setValue(
      this.f("firstName").value.trim().replace(" ", "-")
    );
    this.f("name").setValue(
      this.f("name").value.trim().replace(" ", "-").toUpperCase()
    );

    if (environment.recaptchaEnabled) {
      this.recaptchaSub = this.recaptchaV3Service
        .execute("registerUser")
        .subscribe({
          next: (token) => {
            this.doRegister(token);
          },
          error: () => {
            this.alertService.badRequest(
              "Error trying to verify the ReCaptcha token"
            );
          },
        });
    } else {
      this.doRegister(null);
    }
  }

  doRegister(recaptchaToken: string) {
    this.submitted = true;
    this.loading = true;

    // set empty field to null
    FormHelper.setFcNullIfAllEmpty(this.form_phoneNumber);
    this.registerSub = this.userService
      .registerUser(this.registerForm.value, null, recaptchaToken)
      .pipe(first())
      .subscribe({
        next: (data) => {
          this.authenticationService.saveUserLogin(this.f("email").value);
          this.authenticationService.updateCurrentIdp(data.idp);
          // log the user directly
          this.authenticationService.doB2CLogin(
            data.idp,
            this.f("email").value,
            this.f("password").value,
            this.returnUrl
          );
        },
        error: (err: HttpErrorResponse) => {
          if (err.error && err.error.businessCode) {
            switch (err.error.businessCode) {
              case ErrorBusinessCode.EMAIL_EXISTS:
                this.f("email").setErrors({ exists: true });
                break;
              case ErrorBusinessCode.USER_ACCOUNT_NOT_MANAGED:
                this.f("email").setErrors({ notManaged: true });
                break;
              case ErrorBusinessCode.REFUSED_PASSWORD:
                this.f("password").setErrors({ refusedPassword: true });
                break;
            }
          }
          this.loading = false;
        },
      });
  }

  getBackLoginParams(): any {
    const queryParams = {};
    queryParams[AppConstants.PARAM_CHANGE_USER] = true;
    queryParams[AppConstants.PARAM_RETURN_URL] = this.returnUrl;
    return queryParams;
  }

  viewCguInGoogleDoc() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(IframeDialogComponent, {
        data: {
          title: "EEMO CGU",
          url: this.propHelper.getGooglePreviewUrl(this.cguUrl),
        },
      });
    }
  }
}
